import React, { useEffect, useMemo, useState } from 'react';
import { Search, RefreshCw, ShoppingBag, CheckCircle2, Clock3, ShoppingCart, Ticket } from 'lucide-react';
import { paymentsService } from '../services/api';
import { BrandSpinnerRing } from '../components/SiteBrandedLoader';

type OrderRow = {
  id: string;
  invoiceNumber: string;
  amount: number;
  status: 'PENDING' | 'PAID' | 'FAILED' | 'CANCELLED';
  transactionId?: string | null;
  createdAt: string;
  paidAt?: string | null;
  /** کد کوپن ثبت‌شده روی فاکتور (درگاه پرداخت / کیف پول) */
  couponCodeSnapshot?: string | null;
  /** مبلغ تخفیف کوپن به تومان */
  couponDiscountToman?: number | string | null;
  course?: { id: string; title: string } | null;
  user?: {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    username?: string | null;
    phone?: string | null;
    email?: string | null;
  } | null;
  transactions?: Array<{ id: string; type: string; status: string; createdAt: string }>;
};

const Orders: React.FC = () => {
  const [rows, setRows] = useState<OrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'PENDING' | 'PAID' | 'FAILED' | 'CANCELLED'>('ALL');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 20, totalPages: 1 });

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await paymentsService.getAllCourseInvoices({
        page,
        limit: 20,
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        userId: search.trim() || undefined,
      });
      const data = Array.isArray(res?.data) ? res.data : Array.isArray(res?.invoices) ? res.invoices : [];
      const pagination = res?.meta || res?.pagination || { total: 0, page: 1, limit: 20, totalPages: 1 };
      setRows(data);
      setMeta({
        total: Number(pagination.total || 0),
        page: Number(pagination.page || 1),
        limit: Number(pagination.limit || 20),
        totalPages: Number(pagination.totalPages || 1),
      });
    } catch (err: any) {
      setError(err?.response?.data?.message || 'خطا در دریافت سفارشات');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, statusFilter]);

  const statusBadge = (row: OrderRow) => {
    if (row.status === 'PAID') {
      return { text: 'تکمیل شده (دوره فعال)', cls: 'bg-emerald-100 text-emerald-700' };
    }
    if (row.status === 'PENDING') {
      return { text: 'افزوده به سبد خرید', cls: 'bg-blue-100 text-blue-700' };
    }
    if (row.status === 'FAILED') {
      return { text: 'پرداخت ناموفق', cls: 'bg-rose-100 text-rose-700' };
    }
    return { text: 'لغو شده', cls: 'bg-slate-100 text-slate-700' };
  };

  const stats = useMemo(() => {
    return {
      total: rows.length,
      completed: rows.filter((r) => r.status === 'PAID').length,
      pending: 0,
      cart: rows.filter((r) => r.status === 'PENDING').length,
    };
  }, [rows]);

  const paymentMethodLabel = (row: OrderRow) => {
    const latestTxType = row.transactions?.[0]?.type;
    if (latestTxType === 'WALLET_DEDUCTION') return 'کیف پول';
    if (latestTxType === 'PAYMENT' || latestTxType === 'WALLET_CHARGE') return 'درگاه بانکی';
    if (row.status === 'PAID') return 'کیف پول';
    return '—';
  };

  const invoiceUsedCoupon = (row: OrderRow) => {
    const code = (row.couponCodeSnapshot || '').trim();
    const discount = Number(row.couponDiscountToman ?? 0);
    return Boolean(code) || discount > 0;
  };

  return (
    <div className="space-y-5">
      <div className="rounded-2xl bg-white p-5 shadow-sm sm:p-6">
        <h1 className="text-xl font-black text-gray-900 sm:text-2xl">سفارشات دوره‌ها</h1>
        <p className="mt-1.5 text-sm font-medium text-gray-600">
          همه سفارش‌های دوره کاربران با وضعیت نهایی: تکمیل‌شده یا افزوده به سبد خرید.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div className="rounded-2xl border border-indigo-100 bg-gradient-to-b from-indigo-50 to-white p-4 shadow-sm"><div className="flex items-start justify-between"><div><p className="text-xs font-bold text-indigo-700">نمایش فعلی</p><p className="mt-2 text-2xl font-black text-indigo-900">{stats.total.toLocaleString('fa-IR')}</p></div><span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-100 text-indigo-700"><ShoppingBag className="h-4 w-4" /></span></div></div>
        <div className="rounded-2xl border border-emerald-100 bg-gradient-to-b from-emerald-50 to-white p-4 shadow-sm"><div className="flex items-start justify-between"><div><p className="text-xs font-bold text-emerald-700">تکمیل شده</p><p className="mt-2 text-2xl font-black text-emerald-900">{stats.completed.toLocaleString('fa-IR')}</p></div><span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700"><CheckCircle2 className="h-4 w-4" /></span></div></div>
        <div className="rounded-2xl border border-amber-100 bg-gradient-to-b from-amber-50 to-white p-4 shadow-sm"><div className="flex items-start justify-between"><div><p className="text-xs font-bold text-amber-700">در انتظار پرداخت</p><p className="mt-2 text-2xl font-black text-amber-900">۰</p></div><span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-amber-100 text-amber-700"><Clock3 className="h-4 w-4" /></span></div></div>
        <div className="rounded-2xl border border-blue-100 bg-gradient-to-b from-blue-50 to-white p-4 shadow-sm"><div className="flex items-start justify-between"><div><p className="text-xs font-bold text-blue-700">افزوده به سبد</p><p className="mt-2 text-2xl font-black text-blue-900">{stats.cart.toLocaleString('fa-IR')}</p></div><span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-blue-100 text-blue-700"><ShoppingCart className="h-4 w-4" /></span></div></div>
      </div>

      <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-5">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[16rem] flex-1">
            <Search className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="فیلتر سریع بر اساس شناسه کاربر..."
              className="w-full rounded-xl border border-slate-200 py-2.5 pr-9 pl-3 text-sm"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as any);
              setPage(1);
            }}
            className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm"
          >
            <option value="ALL">همه وضعیت‌ها</option>
            <option value="PAID">تکمیل شده</option>
            <option value="PENDING">افزوده به سبد خرید</option>
            <option value="FAILED">ناموفق</option>
            <option value="CANCELLED">لغو شده</option>
          </select>
          <button
            onClick={() => { setPage(1); setSearch(searchInput.trim()); fetchData(); }}
            className="rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-black text-white"
          >
            اعمال
          </button>
          <button onClick={fetchData} className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs font-black text-slate-700"><RefreshCw className="h-3.5 w-3.5" />بروزرسانی</button>
          <span className="text-xs font-semibold text-slate-500">کل سفارشات: {meta.total.toLocaleString('fa-IR')}</span>
        </div>
      </div>

      {error ? <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{error}</div> : null}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm ring-1 ring-slate-100">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1280px]">
            <thead className="bg-gradient-to-l from-slate-50 via-white to-slate-50/80">
              <tr className="text-right text-[11px] font-black uppercase text-slate-600">
                <th className="px-3 py-3">فاکتور</th>
                <th className="px-3 py-3">کاربر</th>
                <th className="px-3 py-3">دوره</th>
                <th className="px-3 py-3">وضعیت سفارش</th>
                <th className="px-3 py-3">روش پرداخت</th>
                <th className="px-3 py-3">کوپن / تخفیف</th>
                <th className="px-3 py-3">مبلغ</th>
                <th className="px-3 py-3">تاریخ ثبت</th>
                <th className="px-3 py-3">تاریخ پرداخت</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="px-3 py-10 text-center"><BrandSpinnerRing size="sm" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={9} className="px-3 py-10 text-center text-sm text-slate-500">سفارشی یافت نشد</td></tr>
              ) : rows.map((row) => {
                const badge = statusBadge(row);
                const couponSnap = (row.couponCodeSnapshot || '').trim();
                return (
                  <tr key={row.id} className="border-t border-slate-100 text-sm transition hover:bg-indigo-50/30">
                    <td className="px-3 py-3">
                      <p className="font-black text-slate-900">{row.invoiceNumber}</p>
                      <p className="text-xs text-slate-500">{row.transactionId ? `TX: ${row.transactionId}` : 'بدون تراکنش'}</p>
                    </td>
                    <td className="px-3 py-3">
                      <p className="font-bold text-slate-900">{`${row.user?.firstName || ''} ${row.user?.lastName || ''}`.trim() || row.user?.username || 'کاربر'}</p>
                      <p className="text-xs text-slate-500">{row.user?.phone || row.user?.email || '-'}</p>
                    </td>
                    <td className="px-3 py-3">
                      <p className="font-semibold text-slate-800">{row.course?.title || '-'}</p>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${badge.cls}`}>{badge.text}</span>
                    </td>
                    <td className="px-3 py-3 text-xs font-bold text-slate-700">{paymentMethodLabel(row)}</td>
                    <td className="px-3 py-3">
                      {invoiceUsedCoupon(row) ? (
                        <div className="flex flex-col items-start gap-1">
                          {couponSnap ? (
                            <span
                              className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-black text-violet-900 shadow-sm"
                              dir="ltr"
                            >
                              <Ticket className="h-3 w-3 shrink-0 text-violet-700" aria-hidden />
                              {couponSnap}
                            </span>
                          ) : (
                            <span className="text-[10px] font-bold text-slate-500">کد روی فاکتور ثبت نشده</span>
                          )}
                          {Number(row.couponDiscountToman ?? 0) > 0 ? (
                            <span className="text-[11px] font-black text-emerald-800 tabular-nums">
                              −{Number(row.couponDiscountToman).toLocaleString('fa-IR')} ت تخفیف کوپن
                            </span>
                          ) : couponSnap ? (
                            <span className="text-[10px] font-semibold text-slate-500">جزئیات مبلغ در فاکتور خالی است</span>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-xs font-semibold text-slate-400">بدون کوپن</span>
                      )}
                    </td>
                    <td className="px-3 py-3 font-black text-[#1C3B9B]">{Number(row.amount || 0).toLocaleString('fa-IR')} تومان</td>
                    <td className="px-3 py-3 text-xs text-slate-500">{new Date(row.createdAt).toLocaleString('fa-IR')}</td>
                    <td className="px-3 py-3 text-xs text-slate-500">{row.paidAt ? new Date(row.paidAt).toLocaleString('fa-IR') : '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-2.5">
        <p className="text-xs font-semibold text-slate-600">
          صفحه {Number(meta.page || page).toLocaleString('fa-IR')} از {Number(meta.totalPages || 1).toLocaleString('fa-IR')}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold disabled:opacity-50"
          >
            قبلی
          </button>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(Number(meta.totalPages || 1), p + 1))}
            disabled={page >= Number(meta.totalPages || 1)}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold disabled:opacity-50"
          >
            بعدی
          </button>
        </div>
      </div>
    </div>
  );
};

export default Orders;
